import { useRouter } from "next/navigation";
import { ArrowLeftRight } from "lucide-react";

import { GroupOrder, UsersService } from "@/generated";
import { ORG_PERMISSIONS } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { setActiveGroupId } from "@/utils/activeGroup";
import { UUID_RE } from "@/utils/generic";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, dataWrapper } from "@/utils/useRemoteData";
import { queryClient } from "@/utils/queryClient";
import { type GenieTool } from "../registries/tool-registry";
import { useGenieTools } from "../use-genie-tools";

export interface GenieWorkspace {
  id: string;
  name: string;
}

export type WorkspaceMatch =
  | { workspace: GenieWorkspace }
  | { error: string; candidates: GenieWorkspace[] };

export function resolveWorkspace(
  workspaces: GenieWorkspace[],
  query: string
): WorkspaceMatch {
  const q = query.trim().toLowerCase();

  const byId = workspaces.find((w) => w.id.toLowerCase() === q);
  if (byId) return { workspace: byId };

  const exact = workspaces.filter((w) => w.name.trim().toLowerCase() === q);
  if (exact.length === 1) return { workspace: exact[0] };

  const partial = workspaces.filter((w) => w.name.toLowerCase().includes(q));
  if (partial.length === 1) return { workspace: partial[0] };
  if (partial.length > 1) {
    return {
      error: `Several workspaces match "${query}". Pick one by exact name or id.`,
      candidates: partial,
    };
  }
  return { error: `No workspace matches "${query}".`, candidates: workspaces };
}

// Mirrors the sidenav switcher: org admins can switch into workspaces they are
// not a member of, and noxus_workspaces_list hands them those ids, so resolving
// only against the user's own groups would reject them.
async function fetchWorkspaces(canSeeAll: boolean): Promise<GenieWorkspace[]> {
  const page = await dataWrapper(
    canSeeAll ? UsersService.getAllGroups : UsersService.getMyGroups
  )({
    query: {
      page: 1,
      size: 100,
      order_by: GroupOrder.GROUP_NAME,
      sort: "asc",
    },
  });
  return page.items.map((item) => ({
    id: item.group.id,
    name: item.group.name,
  }));
}

// The arg as a status line can say it: a name, never a raw id.
const workspaceName = (value: unknown) =>
  typeof value === "string" && !UUID_RE.test(value) ? value : undefined;

export function useWorkspaceTools() {
  const router = useRouter();
  const { spotUser, spotGroup } = useSpotUser();
  const { hasAllPermissions } = usePermissions();
  const canSeeAllWorkspaces = hasAllPermissions(
    [ORG_PERMISSIONS.WORKSPACE.READ],
    "tenant"
  );

  useGenieTools(() => {
    const tools: GenieTool[] = [
      {
        name: "switch_workspace",
        description:
          "Switch the active workspace. This is the ONLY way to change workspace. There is no workspace URL to navigate to. Accepts a workspace name, or an id from noxus_workspaces_list.",
        // Silently repoints every later tool call at a different workspace, and
        // persists as the user's default. The user confirms the move.
        requires_approval: true,
        parameters: {
          type: "object",
          properties: {
            workspace: {
              type: "string",
              description: "The target workspace's name or id",
            },
          },
          required: ["workspace"],
        },
        execute: async (input) => {
          const query = input.workspace as string;
          const workspaces = await fetchWorkspaces(canSeeAllWorkspaces);
          const match = resolveWorkspace(workspaces, query);
          if ("error" in match) {
            return { error: match.error, workspaces: match.candidates };
          }
          if (match.workspace.id === spotGroup?.id) {
            return { switched: false, workspace: match.workspace.name };
          }

          setActiveGroupId(match.workspace.id);
          // Persist so new tabs and later sessions inherit the selection, the
          // same two-step the workspace switcher in the sidenav does.
          await UsersService.updateMe({
            body: {
              settings: {
                ...spotUser?.settings,
                defaultGroup: match.workspace.id,
              },
            },
          });
          await queryClient.invalidateQueries({ queryKey: [DATA_KEYS.USER] });
          router.push("/");
          return { switched: true, workspace: match.workspace.name };
        },
        display: {
          icon: ArrowLeftRight,
          action: "Switch workspace",
          grant: "Switching workspaces",
          approval: { op: "go", entity: "workspace", name_arg: "workspace" },
          messageStart: (input) =>
            workspaceName(input.workspace)
              ? `Switching to ${workspaceName(input.workspace)}...`
              : "Switching workspace...",
          messageEnd: (input) =>
            workspaceName(input.workspace)
              ? `Switched to ${workspaceName(input.workspace)}`
              : "Switched workspace",
          messageError: "Could not switch workspace",
        },
      },
    ];

    return tools;
  }, [router, spotGroup?.id, spotUser?.settings, canSeeAllWorkspaces]);
}
